import React from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import BusinessRouteErrorBoundary from './BusinessRouteErrorBoundary';
import { useAuth } from '../../contexts/AuthContext';
import { useBusiness } from '../../contexts/BusinessContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { normalizeBusinessRole } from '../../lib/role';
import BusinessSidebar from './BusinessSidebar';
import OverviewDashboard from './OverviewDashboard';
import TicketsManager from './TicketsManager';
import Analytics from './Analytics';
import VoiceCalls from './VoiceCalls';
import AIVoice from './AIVoice/AIVoice';
import AIChatTester from '../Common/AIChatTester';
import SolutionBank from './SolutionBank';
import SolutionBankManager from './SolutionBankManager';
import Customers from './Customers';
import Settings from './Settings';
import StaffManagement from './StaffManagement';
import Billing from './Billing';
import BusinessCommercial from './BusinessCommercial';
import TrialBanner from './TrialBanner';
import CreateSurvey from './CreateSurvey';
import AIAgentDashboard from './AIAgent/AIAgentDashboard';
import AutoResolution from './AIAgent/AutoResolution';
import KnowledgeBase from './AIAgent/KnowledgeBase';
import AIAnalytics from './AIAgent/AIAnalytics';
import LearningCenter from './AIAgent/LearningCenter';
import ShowroomManager from '../Marketplace/ShowroomManager';
import MarketplaceFeed from '../Marketplace/MarketplaceFeed';

// Manager Profile Components
import ManageOperators from './Manager/ManageOperators';
import TicketManagement from './Manager/TicketManagement';
import BusinessCategoryManager from './Manager/BusinessCategoryManager';

// Operator Profile Components
import UnifiedAgentDesktop from './Operator/UnifiedAgentDesktop';
import AIVoiceMonitor from './Operator/AIVoiceMonitor';
import AIVoiceSimulatorEnhanced from './Operator/AIVoiceSimulatorEnhanced';
import AIChatSimulatorEnhanced from './Operator/AIChatSimulatorEnhanced';

// Analyst Profile Components
import DataHub from './Analyst/DataHub';
import AnalyticsDashboard from './Analyst/AnalyticsDashboard';

// Director Profile Components
import StrategyDashboard from './Director/StrategyDashboard';
import DirectorDashboard from './Director/DirectorDashboard';

// Executive Profile Components
import ExecutiveDashboard from './Executive/ExecutiveDashboard';
import ExecutiveStrategicDashboard from './Executive/ExecutiveStrategicDashboard';

// IT Profile Components
import IntegrationHub from './IT/IntegrationHub';
import ITDashboard from './IT/ITDashboard';

// CRM Components
import VoIPCaller from '../CRM/VoIPCaller';
import ContactManager from '../CRM/ContactManager';
import PushNotificationCenter from '../CRM/PushNotificationCenter';
import FriendInvitation from '../Common/FriendInvitation';

// AI Metrics & Management Components
import AIMetricsDashboard from './AIMetrics/AIMetricsDashboard';
import EscalationManager from './Escalations/EscalationManager';
import SchedulingManager from './Scheduling/SchedulingManager';
import StatusUpdates from './StatusUpdates/StatusUpdates';
import SchedulingConfig from './Scheduling/SchedulingConfig';

const BusinessDashboard = () => {
  const { user, loading } = useAuth();
  const { currentBusiness } = useBusiness();
  const { t } = useLanguage();

  // Show loading if auth is still loading
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
          <h2 className="text-xl font-semibold mb-2">{t('common.loading')}</h2>
          <p className="text-gray-600">{t('dash.loadingDashboardWait')}</p>
        </div>
      </div>
    );
  }

  // Role-based access control
  const hasAccess = (requiredRoles) => {
    return requiredRoles.includes(normalizeBusinessRole(user?.role));
  };

  // If no business context, show loading or error (except for super_admin)
  const _normRole = normalizeBusinessRole(user?.role);
  if (!currentBusiness && _normRole !== 'customer' && _normRole !== 'super_admin') {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto mb-4"></div>
          <h2 className="text-xl font-semibold mb-2">{t('dash.loadingBusinessData')}</h2>
          <p className="text-gray-600">{t('dash.loadingBusinessInfo')}</p>
        </div>
      </div>
    );
  }

  const location = useLocation();
  return (
    <div className="flex h-screen bg-gradient-to-br from-white via-green-50 to-purple-50">
      <BusinessSidebar />
      
      <div className="flex-1 overflow-hidden flex flex-col">
        <TrialBanner />
        {/* DR-010/gap-A: route container must scroll vertically so every routed page is fully reachable
            on every role/viewport. Pages that provide their own inner overflow still work (h-full fills
            this container). min-h-0 lets the flex child shrink so overflow-y-auto engages. */}
        <div className="flex-1 min-h-0 overflow-y-auto">
        <BusinessRouteErrorBoundary resetKey={location.pathname}>
        <Routes>
          <Route path="/" element={<OverviewDashboard />} />
          {/* Servisfy Status & Updates — universal (all Business staff roles; no entitlement) */}
          <Route path="/status" element={<StatusUpdates />} />

          {/* Customer Profile Routes */}
          {hasAccess(['customer']) && (
            <>
              <Route path="/ai-voice-monitor" element={<AIVoiceMonitor businessId={currentBusiness?.brandId || ''} />} />
              <Route path="/ai-voice-simulator" element={<AIVoiceSimulatorEnhanced />} />
              <Route path="/crm-voip" element={<VoIPCaller />} />
              <Route path="/crm-contacts" element={<ContactManager />} />
              <Route path="/marketplace" element={<MarketplaceFeed />} />
              {/* REMOVED: /take-survey. SurveyCenter calls GET /api/v1/surveys/available --
                  the CUSTOMER-plane MGR-09 respondent-eligibility endpoint -- so this route put a
                  Customer surface behind a Business URL (Change-Control Constitution 5,
                  AUTH_ROLE_CROSSOVER=NO). CUSTOMER_WEB=NONE: the supported respondent planes are the
                  Android and iOS Customer apps. Not re-gated and not replaced. The Business survey
                  authority is /business/create-survey (CreateSurvey + SurveyManager), unchanged. */}
              <Route path="/invite" element={<FriendInvitation />} />
            </>
          )}
          
          {/* Operator Enhanced Routes */}
          {hasAccess(['operator']) && (
            <>
              <Route path="/unified-desktop" element={<UnifiedAgentDesktop />} />
              <Route path="/ai-voice-simulator" element={<AIVoiceSimulatorEnhanced />} />
              <Route path="/ai-chat-simulator" element={<AIChatSimulatorEnhanced />} />
              <Route path="/ai-metrics" element={<AIMetricsDashboard />} />
              <Route path="/escalations" element={<EscalationManager />} />
              <Route path="/tickets" element={<TicketsManager />} />
              <Route path="/scheduling" element={<SchedulingManager />} />
              <Route path="/customers" element={<Customers />} />
              {/* Contact Manager (embedded in Customers) row actions open the VoIP caller for the selected contact. */}
              <Route path="/crm-voip" element={<VoIPCaller />} />
              <Route path="/solution-bank" element={<SolutionBank />} />
              <Route path="/voice-calls" element={<VoiceCalls />} />
              <Route path="/knowledge-base" element={<KnowledgeBase />} />
              <Route path="/learning-center" element={<LearningCenter />} />
            </>
          )}
          
          {/* Manager Enhanced Routes */}
          {hasAccess(['manager']) && (
            <>
              <Route path="/manage-operators" element={<ManageOperators />} />
              <Route path="/voice-calls" element={<VoiceCalls />} />
              <Route path="/staff-management" element={<StaffManagement />} />{/* M24 — Manager may manage staff roles */}
              <Route path="/category-manager" element={<BusinessCategoryManager />} />
              <Route path="/ai-voice-simulator" element={<AIVoiceSimulatorEnhanced />} />
              <Route path="/ai-chat-simulator" element={<AIChatSimulatorEnhanced />} />
              <Route path="/ai-metrics" element={<AIMetricsDashboard />} />
              <Route path="/escalations" element={<EscalationManager />} />
              <Route path="/solution-review" element={<Navigate to="/business/solution-bank?tab=review" replace />} />{/* legacy deep link -> canonical Solution Bank Review tab */}
              <Route path="/analytics" element={<Analytics />} />
              <Route path="/solution-bank" element={<SolutionBank />} />
              <Route path="/solution-bank-manage" element={<SolutionBankManager />} />
              <Route path="/ticket-management" element={<TicketManagement />} />
              <Route path="/customers" element={<Customers />} />
              {/* Contact Manager row actions: caller + the tickets view that consumes the new-conversation handoff. */}
              <Route path="/crm-voip" element={<VoIPCaller />} />
              <Route path="/tickets" element={<TicketsManager />} />
              <Route path="/scheduling" element={<SchedulingManager />} />
              <Route path="/scheduling-config" element={<SchedulingConfig />} />
              <Route path="/showroom" element={<ShowroomManager />} />
              <Route path="/create-survey" element={<CreateSurvey />} />
              <Route path="/ai-agent" element={<AIAgentDashboard />} />
              <Route path="/push-notifications" element={<PushNotificationCenter />} />
              <Route path="/knowledge-base" element={<KnowledgeBase />} />
            </>
          )}

          {/* Analyst Enhanced Routes */}
          {hasAccess(['analyst']) && (
            <>
              <Route path="/data-hub" element={<DataHub />} />
              <Route path="/voice-calls" element={<VoiceCalls />} />
              <Route path="/analytics-dashboard" element={<AnalyticsDashboard />} />
              <Route path="/analytics" element={<Analytics />} />
              <Route path="/ai-metrics" element={<AIMetricsDashboard />} />
              <Route path="/ai-analytics" element={<AIAnalytics />} />
              {/* Scheduling route intentionally removed for analyst — backend SCHED_BOOK_ROLES denies analyst. */}
            </>
          )}

          {/* Director Enhanced Routes */}
          {hasAccess(['director']) && (
            <>
              <Route path="/director-dashboard" element={<DirectorDashboard />} />
              <Route path="/voice-calls" element={<VoiceCalls />} />
              <Route path="/strategy-dashboard" element={<StrategyDashboard />} />
              <Route path="/analytics" element={<Analytics />} />
              <Route path="/ai-metrics" element={<AIMetricsDashboard />} />
              {/* Scheduling route intentionally removed for director — backend SCHED_BOOK_ROLES denies director. */}
            </>
          )}

          {/* Executive Enhanced Routes */}
          {hasAccess(['executive']) && (
            <>
              <Route path="/executive-dashboard" element={<ExecutiveDashboard />} />
              <Route path="/voice-calls" element={<VoiceCalls />} />
              <Route path="/executive-strategic" element={<ExecutiveStrategicDashboard />} />
              <Route path="/analytics" element={<Analytics />} />
              <Route path="/ai-metrics" element={<AIMetricsDashboard />} />
              {/* Scheduling route intentionally removed for executive — backend SCHED_BOOK_ROLES denies executive. */}
            </>
          )}

          {/* IT Enhanced Routes */}
          {hasAccess(['it']) && (
            <>
              <Route path="/integration-hub" element={<IntegrationHub />} />
              <Route path="/voice-calls" element={<VoiceCalls />} />
              <Route path="/it-dashboard" element={<ITDashboard />} />
              <Route path="/analytics" element={<Analytics />} />
              <Route path="/ai-metrics" element={<AIMetricsDashboard />} />
              <Route path="/solution-bank" element={<SolutionBank />} />
              <Route path="/settings" element={<Settings />} />
              {/* Scheduling routes intentionally removed for IT/Integration — backend 403s it on both. */}
            </>
          )}

          {/* Admin Access Routes */}
          {hasAccess(['admin']) && (
            <>
              <Route path="/staff-management" element={<StaffManagement />} />
              <Route path="/billing" element={<Billing />} />
              <Route path="/plan-capabilities" element={<BusinessCommercial />} />
              <Route path="/scheduling" element={<SchedulingManager />} />
              <Route path="/scheduling-config" element={<SchedulingConfig />} />
              <Route path="/showroom" element={<ShowroomManager />} />
              <Route path="/create-survey" element={<CreateSurvey />} />
              <Route path="/category-manager" element={<BusinessCategoryManager />} />
              <Route path="/ai-voice-simulator" element={<AIVoiceSimulatorEnhanced />} />
              <Route path="/ai-chat-simulator" element={<AIChatSimulatorEnhanced />} />
              <Route path="/ai-metrics" element={<AIMetricsDashboard />} />
              <Route path="/escalations" element={<EscalationManager />} />
              <Route path="/solution-review" element={<Navigate to="/business/solution-bank?tab=review" replace />} />{/* legacy deep link -> canonical Solution Bank Review tab */}
              <Route path="/analytics" element={<Analytics />} />
              <Route path="/solution-bank" element={<SolutionBank />} />
              <Route path="/solution-bank-manage" element={<SolutionBankManager />} />
              <Route path="/tickets" element={<TicketsManager />} />
              <Route path="/customers" element={<Customers />} />
              {/* Contact Manager (embedded in Customers) row actions open the VoIP caller for the selected contact. */}
              <Route path="/crm-voip" element={<VoIPCaller />} />
              <Route path="/settings" element={<Settings />} />
              <Route path="/voice-calls" element={<VoiceCalls />} />
              <Route path="/ai-voice" element={<AIVoice />} />
              <Route path="/ai-chat-tester" element={<AIChatTester />} />
              <Route path="/ai-agent" element={<AIAgentDashboard />} />
              <Route path="/auto-resolution" element={<AutoResolution />} />
              <Route path="/knowledge-base" element={<KnowledgeBase />} />
              <Route path="/ai-analytics" element={<AIAnalytics />} />
              <Route path="/learning-center" element={<LearningCenter />} />
              <Route path="/push-notifications" element={<PushNotificationCenter />} />
              <Route path="/invite" element={<FriendInvitation />} />
            </>
          )}

          {/* Owner/SuperAdmin is a SEPARATE plane (/superadmin-portal, OTP/JWT). Its routes
              live there only — never inside the Business plane. */}

          <Route path="*" element={<Navigate to="/business" replace />} />
        </Routes>
        </BusinessRouteErrorBoundary>
        </div>
      </div>
    </div>
  );
};

export default BusinessDashboard;